/**
 * Settings Feature Routes
 */
import type { RouteRecordRaw } from 'vue-router';
import i18next from 'i18next';
import { WIZARD_STEPS, SETTINGS_CONFIGURE_BASE } from './wizardSteps';

/**
 * Standalone, chrome-free welcome/checklist route — deliberately NOT nested
 * under `/app-settings` (which renders inside `SettingsLayout.vue`) or under
 * the root `AppLayout` (sidebar/header/recall banner). Registered as a
 * top-level sibling in `router/index.ts`, same pattern as `authRoutes`.
 */
export const settingsWelcomeRoute: RouteRecordRaw = {
  path: '/app-settings/welcome',
  name: 'settings-welcome',
  component: () => import('./views/SettingsWelcomeView.vue'),
  meta: {
    requiresAuth: true,
    requiredEntitlement: 'SETTINGS',
    layout: 'blank',
    title: () => i18next.t('settings.welcome.pageTitle'),
  },
};

/**
 * Component loader per wizard step key, reused as-is from `settingsRoutes`
 * below — the stepper page renders the exact same view components, just at
 * a different route base, so both route trees stay in sync automatically as
 * long as this map is kept alongside `settingsRoutes`' children.
 */
const STEP_COMPONENT_LOADERS: Record<string, () => Promise<unknown>> = {
  'allergy-management': () => import('./views/AllergyManagementView.vue'),
  'milk-type': () => import('./views/MilkTypeView.vue'),
  'hospital-config': () => import('./views/HospitalConfigView.vue'),
  'expiry-rules': () => import('./views/ExpiryRulesView.vue'),
  'roles-permissions': () => import('./views/RolesPermissionsView.vue'),
  'approval-compliance': () => import('./views/ApprovalComplianceView.vue'),
  'milk-formula-storage': () => import('./views/MilkFormulaStorageView.vue'),
  'printer-config': () => import('./views/PrinterConfigView.vue'),
  'idp-federation': () => import('./views/IdpFederationView.vue'),
  'interoperability-config': () => import('./views/InteroperabilityConfigView.vue'),
  localization: () => import('./views/LocalizationView.vue'),
};

const STEP_TITLE_KEYS: Record<string, string> = {
  'allergy-management': 'settings.nav.allergyManagement',
  'milk-type': 'feed.milkType',
  'hospital-config': 'settings.nav.hospitalConfig',
  'expiry-rules': 'settings.nav.expiryRules',
  'roles-permissions': 'settings.dashboard.rolesTitle',
  'approval-compliance': 'settings.nav.approvalCompliance',
  'milk-formula-storage': 'settings.nav.milkFormulaStorage',
  'printer-config': 'settings.nav.printerConfig',
  'idp-federation': 'settings.idpFederation.pageTitle',
  'interoperability-config': 'interoperability.config.pageTitle',
  localization: 'smartSearch.locale.title',
};

/**
 * Dedicated stepper page — reuses the same step-view components as
 * `settingsRoutes` below, unmodified, at `/app-settings/configure/<key>`
 * instead of `/app-settings/<key>`. Standalone-page behaviour (SettingsLayout,
 * the paths above) is untouched; see `wizardSteps.ts`'s `nextStepPath` for how
 * each step's own internal "Next" navigation resolves to the matching base.
 */
export const settingsConfigureRoute: RouteRecordRaw = {
  path: SETTINGS_CONFIGURE_BASE,
  component: () => import('./views/SettingsConfigureLayout.vue'),
  meta: {
    requiresAuth: true,
    requiredEntitlement: 'SETTINGS',
    layout: 'blank',
  },
  children: [
    { path: '', redirect: { name: `settings-configure-${WIZARD_STEPS[0].key}` } },
    ...WIZARD_STEPS.map((step) => ({
      path: step.key,
      name: `settings-configure-${step.key}`,
      component: STEP_COMPONENT_LOADERS[step.key],
      meta: {
        stepKey: step.key,
        title: () => i18next.t(STEP_TITLE_KEYS[step.key]),
      },
    })),
  ],
};

export const settingsRoutes: RouteRecordRaw[] = [
  {
    path: '/app-settings',
    component: () => import('./views/SettingsLayout.vue'),
    meta: {
      requiredEntitlement: 'SETTINGS',
    },
    children: [
      {
        path: '',
        name: 'settings',
        redirect: { name: 'settings-dashboard' },
      },
      {
        path: 'dashboard',
        name: 'settings-dashboard',
        component: () => import('./views/SettingsDashboardView.vue'),
        meta: {
          title: () => i18next.t('settings.dashboard.pageTitle'),
          breadcrumbs: [
            { label: () => i18next.t('settings.dashboard.pageTitle') },
          ],
        },
      },
      {
        path: 'allergy-management',
        name: 'settings-allergy-management',
        component: () => import('./views/AllergyManagementView.vue'),
        meta: {
          title: () => i18next.t('settings.nav.allergyManagement'),
          breadcrumbs: [
            { label: () => i18next.t('settings.dashboard.pageTitle'), to: '/app-settings/dashboard' },
            { label: () => i18next.t('settings.nav.allergyManagement') },
          ],
        },
      },
      {
        path: 'milk-type',
        name: 'settings-milk-type',
        component: () => import('./views/MilkTypeView.vue'),
        meta: {
          title: () => i18next.t('feed.milkType'),
          breadcrumbs: [
            { label: () => i18next.t('settings.dashboard.pageTitle'), to: '/app-settings/dashboard' },
            { label: () => i18next.t('feed.milkType') },
          ],
        },
      },
      {
        path: 'hospital-config',
        name: 'settings-hospital-config',
        component: () => import('./views/HospitalConfigView.vue'),
        meta: {
          title: () => i18next.t('settings.nav.hospitalConfig'),
          breadcrumbs: [
            { label: () => i18next.t('settings.dashboard.pageTitle'), to: '/app-settings/dashboard' },
            { label: () => i18next.t('settings.nav.hospitalConfig') },
          ],
        },
      },
      {
        path: 'expiry-rules',
        name: 'settings-expiry-rules',
        component: () => import('./views/ExpiryRulesView.vue'),
        meta: {
          title: () => i18next.t('settings.nav.expiryRules'),
          breadcrumbs: [
            { label: () => i18next.t('settings.dashboard.pageTitle'), to: '/app-settings/dashboard' },
            { label: () => i18next.t('settings.nav.expiryRules') },
          ],
        },
      },
      {
        path: 'roles-permissions',
        name: 'settings-roles-permissions',
        component: () => import('./views/RolesPermissionsView.vue'),
        meta: {
          title: () => i18next.t('settings.dashboard.rolesTitle'),
          breadcrumbs: [
            { label: () => i18next.t('settings.dashboard.pageTitle'), to: '/app-settings/dashboard' },
            { label: () => i18next.t('settings.dashboard.rolesTitle') },
          ],
        },
      },
      {
        path: 'printer-config',
        name: 'settings-printer-config',
        component: () => import('./views/PrinterConfigView.vue'),
        meta: {
          title: () => i18next.t('settings.nav.printerConfig'),
          breadcrumbs: [
            { label: () => i18next.t('settings.dashboard.pageTitle'), to: '/app-settings/dashboard' },
            { label: () => i18next.t('settings.nav.printerConfig') },
          ],
        },
      },
      {
        path: 'approval-compliance',
        name: 'settings-approval-compliance',
        component: () => import('./views/ApprovalComplianceView.vue'),
        meta: {
          title: () => i18next.t('settings.nav.approvalCompliance'),
          breadcrumbs: [
            { label: () => i18next.t('settings.dashboard.pageTitle'), to: '/app-settings/dashboard' },
            { label: () => i18next.t('settings.nav.approvalCompliance') },
          ],
        },
      },
      {
        path: 'localization',
        name: 'settings-localization',
        component: () => import('./views/LocalizationView.vue'),
        meta: {
          title: () => i18next.t('smartSearch.locale.title'),
          breadcrumbs: [
            { label: () => i18next.t('settings.dashboard.pageTitle'), to: '/app-settings/dashboard' },
            { label: () => i18next.t('smartSearch.locale.title') },
          ],
        },
      },
      {
        path: 'milk-formula-storage',
        name: 'settings-milk-formula-storage',
        component: () => import('./views/MilkFormulaStorageView.vue'),
        meta: {
          title: () => i18next.t('settings.nav.milkFormulaStorage'),
          breadcrumbs: [
            { label: () => i18next.t('settings.dashboard.pageTitle'), to: '/app-settings/dashboard' },
            { label: () => i18next.t('settings.nav.milkFormulaStorage') },
          ],
        },
      },
      {
        path: 'idp-federation',
        name: 'settings-idp-federation',
        component: () => import('./views/IdpFederationView.vue'),
        meta: {
          title: () => i18next.t('settings.idpFederation.pageTitle'),
          breadcrumbs: [
            { label: () => i18next.t('settings.dashboard.pageTitle'), to: '/app-settings/dashboard' },
            { label: () => i18next.t('settings.idpFederation.pageTitle') },
          ],
        },
      },
      {
        path: 'interoperability-config',
        name: 'settings-interoperability-config',
        component: () => import('./views/InteroperabilityConfigView.vue'),
        meta: {
          title: () => i18next.t('interoperability.config.pageTitle', 'Interoperability Configuration'),
          breadcrumbs: [
            { label: () => i18next.t('settings.dashboard.pageTitle'), to: '/app-settings/dashboard' },
            { label: () => i18next.t('interoperability.config.pageTitle', 'Interoperability Configuration') },
          ],
        },
      },
    ],
  },
];
